import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Bitte gib deinen Namen ein.").max(100),
  email: z.string().trim().email("Bitte gib eine gültige E-Mail-Adresse ein.").max(255),
  phone: z.string().trim().max(30).optional(),
  service: z.string().min(1, "Bitte wähle einen Service aus."),
  contactMethod: z.enum(["email", "phone"], { errorMap: () => ({ message: "Bitte wähle eine Kontaktart." }) }),
  callTime: z.enum(["vormittags", "mittags", "nachmittags"]).optional(),
  message: z.string().trim().min(10, "Bitte gib eine Nachricht ein (mind. 10 Zeichen).").max(2000),
  consent: z.literal(true, { errorMap: () => ({ message: "Bitte stimme der Datenverarbeitung zu." }) }),
}).refine((data) => data.contactMethod !== "phone" || !!data.callTime, {
  message: "Bitte wähle eine bevorzugte Tageszeit.",
  path: ["callTime"],
});

type ContactFormValues = z.infer<typeof contactSchema>;

const services = [
  "Corporate Identity",
  "Webseiten-Erstellung",
  "Medienproduktion",
  "Social Media Marketing",
  "360° Komplettpaket",
  "Sonstiges",
];

const Kontakt = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", phone: "", service: "", contactMethod: undefined, callTime: undefined, message: "", consent: false as unknown as true },
  });

  const onSubmit = async (data: ContactFormValues) => {
    const id = crypto.randomUUID();

    try {
      // Send notification to info@4facemedia.de
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          recipientEmail: data.email,
          idempotencyKey: `contact-notify-${id}`,
          templateData: {
            name: data.name,
            email: data.email,
            phone: data.phone || "",
            service: data.service,
            contactMethod: data.contactMethod === "phone" ? "Telefon" : "E-Mail",
            callTime: data.contactMethod === "phone" ? (data.callTime || "") : "",
            message: data.message,
          },
        },
      });

      // Send confirmation to the user
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: data.email,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: data.name },
        },
      });

      setSubmitted(true);
      toast({
        title: "Nachricht gesendet!",
        description: "Vielen Dank für deine Anfrage. Wir melden uns in Kürze bei dir.",
      });
      form.reset();
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error("Failed to send contact form:", error);
      toast({
        title: "Fehler",
        description: "Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Kontakt – Kreativagentur Bochum | Videoproduktion & Webdesign anfragen" description="Starte dein nächstes Projekt mit 4Facemedia aus Bochum. Videoproduktion, Webdesign, Branding & Social Media Marketing deutschlandweit. Jetzt unverbindlich anfragen!" path="/kontakt" />
      <Header />

      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <span className="text-primary text-sm font-medium tracking-wider uppercase">Sag Hallo</span>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mt-4 mb-6">
              Lass uns <span className="text-gradient">reden.</span>
            </h1>
            <p className="text-xl text-foreground/85 max-w-2xl">
              Du hast ein Projekt im Kopf? Ob aus Bochum, dem Ruhrgebiet oder ganz Deutschland: Schreib uns, wir freuen uns auf deine Nachricht.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-28">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <ScrollReveal>
                <div className="p-8 md:p-10 rounded-3xl bg-gradient-card border border-border/50">
                  <h2 className="text-2xl font-bold text-foreground mb-8">Kontaktformular</h2>

                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Dein Name" className="bg-muted/30 border-border h-12 rounded-xl" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">E-Mail *</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="deine@email.de" className="bg-muted/30 border-border h-12 rounded-xl" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Telefon</FormLabel>
                              <FormControl>
                                <Input placeholder="+49 ..." className="bg-muted/30 border-border h-12 rounded-xl" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="service"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Service *</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="bg-muted/30 border-border h-12 rounded-xl">
                                    <SelectValue placeholder="Bitte wählen..." />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {services.map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="contactMethod"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-foreground">Wie möchtest du kontaktiert werden? *</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-col sm:flex-row gap-4"
                              >
                                <label className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3 cursor-pointer flex-1 hover:border-primary/50 transition-colors">
                                  <RadioGroupItem value="email" />
                                  <span className="text-foreground">Per E-Mail</span>
                                </label>
                                <label className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3 cursor-pointer flex-1 hover:border-primary/50 transition-colors">
                                  <RadioGroupItem value="phone" />
                                  <span className="text-foreground">Telefonisch</span>
                                </label>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch("contactMethod") === "phone" && (
                        <FormField
                          control={form.control}
                          name="callTime"
                          render={({ field }) => (
                            <FormItem className="space-y-3">
                              <FormLabel className="text-foreground">Bevorzugte Tageszeit *</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                                >
                                  {[
                                    { value: "vormittags", label: "Vormittags" },
                                    { value: "mittags", label: "Mittags" },
                                    { value: "nachmittags", label: "Nachmittags" },
                                  ].map((opt) => (
                                    <label key={opt.value} className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3 cursor-pointer hover:border-primary/50 transition-colors">
                                      <RadioGroupItem value={opt.value} />
                                      <span className="text-foreground">{opt.label}</span>
                                    </label>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">Nachricht *</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Erzähl uns von deinem Projekt..."
                                rows={5}
                                className="bg-muted/30 border-border rounded-xl resize-none"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="consent"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-xl border border-border bg-muted/30 p-4">
                            <FormControl>
                              <Checkbox
                                checked={field.value as unknown as boolean}
                                onCheckedChange={field.onChange}
                                className="mt-0.5"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-snug">
                              <FormLabel className="text-sm text-foreground/90 font-normal cursor-pointer">
                                Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage intern gespeichert &amp; weiterverwendet werden. Weitere Informationen findest du in unserer{" "}
                                <Link to="/datenschutz" className="text-primary hover:underline">Datenschutzerklärung</Link>.
                              </FormLabel>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-primary hover:bg-primary/80 text-primary-foreground h-14 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitted || !form.watch("consent")}
                      >
                        {submitted ? "Gesendet ✓" : (
                          <>Nachricht senden <Send className="ml-2" size={18} /></>
                        )}
                      </Button>
                    </form>
                  </Form>
                </div>
              </ScrollReveal>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <ScrollReveal delay={200}>
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-6">Direkt erreichen</h3>
                    <div className="space-y-6">
                      {([
                        { icon: Mail, label: "E-Mail", value: "info@4facemedia.de", href: "mailto:info@4facemedia.de" },
                        { icon: Phone, label: "Telefon", value: "+49 173 235 4407", href: "tel:+491732354407" },
                        
                      ] as { icon: typeof Mail; label: string; value: string; href: string }[]).map(({ icon: Icon, label, value, href }) => (
                        <div key={label} className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon size={20} className="text-primary" />
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">{label}</div>
                            {href ? (
                              <a href={href} className="text-foreground font-medium hover:text-primary transition-colors">{value}</a>
                            ) : (
                              <div className="text-foreground font-medium">{value}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-8 rounded-2xl bg-gradient-card border border-border/50">
                    <h4 className="font-semibold text-foreground mb-3">Schnelle Antwort</h4>
                    <p className="text-base text-foreground/85 leading-relaxed">
                      Wir antworten in der Regel innerhalb von 48 Stunden auf Anfragen. Für dringende Projekte erreichst du uns auch telefonisch.
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Kontakt;
